import React, { useContext } from 'react';
import { Box, Heading, Text, VStack, Button, Flex } from 'src/components/ui';
import { useNavigate } from 'react-router-dom';
import CustomModal from '../../common/CustomModal';
import PlanCard from './PlanCard';
import UserContext from 'src/contexts/user/UserContext';
import authService from 'src/services/auth';
import {
	formatPrice,
	getPlanIntervalQty,
	getPlanIntervalText,
	getPlanPrice,
	getRemainingTime,
} from 'src/lib/utils/billing';
import { BillingStatus } from 'src/models/account/interfaces';

interface TrialEndingModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const TrialEndingModal: React.FC<TrialEndingModalProps> = ({
	isOpen,
	onClose,
}) => {
	const navigate = useNavigate();
	const { subscription, currentPlan, account } = useContext(UserContext);
	if (!subscription || !currentPlan) return null;

	const isTrialOver =
		subscription?.billingStatus === BillingStatus.TrialExpired;
	const isInactive = subscription?.billingStatus === BillingStatus.Inactive;
	const planName = currentPlan?.name;
	const freeTrialEnd = new Date(subscription.freeTrialEnd);
	const timeRemaining = getRemainingTime(freeTrialEnd);
	const price = getPlanPrice(currentPlan.prices, account?.subscription?.planId);
	const intervalQty = getPlanIntervalQty(
		currentPlan.prices,
		account?.subscription?.planId,
	);
	const intervalText = getPlanIntervalText(intervalQty ?? 1);

	const titleText = isInactive
		? 'Your account is paused!'
		: isTrialOver
			? 'Your free trial is over!'
			: `Your trial period will end in ${timeRemaining}`;

	const subtitleText =
		isTrialOver || isInactive
			? `To continue enjoying FusionAds.ai, provide now your payment method. FusionAds.ai ${planName} offers a bundle of features to streamline your marketing campaigns.`
			: `To continue enjoying FusionAds.ai before your trial ends, FusionAds.ai ${planName} offers a bundle of features to streamline your marketing campaigns.`;

	const handleLogout = async () => {
		try {
			await authService.logout();
			navigate('/login');
		} catch (error) {
			console.error('Error during logout:', error);
			authService.cleanupSession();
			navigate('/login');
		}
	};

	return (
		<CustomModal
			isOpen={isOpen}
			onClose={onClose}
			size="3xl"
			header={
				<Box textAlign="center" w="full" h="full">
					<Heading size="lg">{titleText}</Heading>
				</Box>
			}
			footer={
				isTrialOver || isInactive ? (
					<Flex justifyContent="center" w="full">
						<Button variant="gray" onClick={handleLogout} size="md">
							Log out
						</Button>
					</Flex>
				) : null
			}
			headerBgColor="orange.base"
			headerTextColor="white"
			withoutCloseButton={isTrialOver || isInactive}
			headerBgImage="/fusion-header.png"
		>
			<VStack mx="auto" p={6} maxW="lg" spacing={6}>
				<Text fontSize="md" color="gray.700" align="center">
					{subtitleText}
				</Text>
				<PlanCard
					title={currentPlan.name}
					features={currentPlan.features ?? []}
					price={formatPrice(price ?? 0)}
					status={subscription.billingStatus}
					isSelected
					hasPaymentMethod={Boolean(subscription.paymentMethod?.card?.last4)}
					intervalText={intervalText}
				/>
			</VStack>
		</CustomModal>
	);
};

export default TrialEndingModal;
